import React, { useState, useEffect, useCallback } from 'react';
import './TrainerDashboard.css';
import Sidebar from '../../components/Sidebar';
import SvgIcon from '../../components/SvgIcon';
import AddToCalendarDropdown from '../../components/AddToCalendarDropdown';
import NotificationCentre from '../../components/NotificationCentre/NotificationCentre';

const API_BASE = process.env.REACT_APP_API_URL || '';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getCalendarWeeks(year, month) {
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay();

  const gridStartDate = new Date(firstDay);
  gridStartDate.setDate(firstDay.getDate() - startOffset);

  const weeks = [];
  const currentDate = new Date(gridStartDate);

  for (let w = 0; w < 6; w++) {
    const weekDates = [];
    for (let d = 0; d < 7; d++) {
      weekDates.push(`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(weekDates);
  }

  const lastWeekHasCurrentMonth = weeks[5].some(d => {
    const m = Number(d.split('-')[1]) - 1;
    return m === month;
  });
  if (!lastWeekHasCurrentMonth) {
    weeks.pop();
  }

  return weeks;
}

const TrainerDashboard = ({ user, onLogout, onSwitchView, initialTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState(initialTab); // 'overview' | 'calendar' | 'attendance' | 'profile'

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [overviewViewMode, setOverviewViewMode] = useState('grouped'); // 'grouped' | 'table' | 'cards'
  const [timeframeTab, setTimeframeTab] = useState('upcoming'); // 'upcoming' | 'past'
  const [expandedCourses, setExpandedCourses] = useState({});
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Roster modal state
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);

  // Profile edit state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [trainerProfile, setTrainerProfile] = useState(null);

  // First Login Password Reset Modal state
  const [firstLoginModalOpen, setFirstLoginModalOpen] = useState(user?.isFirstLogin === true || user?.mustResetPassword === true);
  const [firstLoginPassword, setFirstLoginPassword] = useState('');
  const [firstLoginConfirmPassword, setFirstLoginConfirmPassword] = useState('');
  const [firstLoginError, setFirstLoginError] = useState('');
  const [firstLoginSubmitting, setFirstLoginSubmitting] = useState(false);


  // Attendance Management state
  const [selectedAttendanceSchedule, setSelectedAttendanceSchedule] = useState(null);
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(null);
  const [activeSessionCode, setActiveSessionCode] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [codeDuration, setCodeDuration] = useState(10);
  const [selectedValidityDuration, setSelectedValidityDuration] = useState(10); // dynamic presets
  const [availableValidityDurations, setAvailableValidityDurations] = useState([10]);

  // Dynamically load allowed attendance code validity durations from admin system settings
  const fetchAttendanceSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings/public-summary`);
      const data = await res.json();
      if (data.status === 'success' && Array.isArray(data.data?.allowedAttendanceDurations)) {
        const allowed = data.data.allowedAttendanceDurations;
        const defaultDur = data.data.defaultAttendanceDuration || allowed[0] || 10;
        setAvailableValidityDurations(allowed);
        setSelectedValidityDuration(prev => (allowed.includes(prev) ? prev : defaultDur));
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchAttendanceSettings();
  }, [fetchAttendanceSettings]);

  // Re-fetch attendance settings whenever a course attendance view is opened
  useEffect(() => {
    if (selectedAttendanceSchedule) {
      fetchAttendanceSettings();
    }
  }, [selectedAttendanceSchedule, fetchAttendanceSettings]);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [confirmRegenModalOpen, setConfirmRegenModalOpen] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [attendanceList, setAttendanceList] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [checkInCodeInput, setCheckInCodeInput] = useState('');
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);
  const [checkInMsg, setCheckInMsg] = useState(null);
  const [attendanceFilter, setAttendanceFilter] = useState('all'); // 'all' | 'Present' | 'Absent' | 'Late'
  const [attendeeSearchQuery, setAttendeeSearchQuery] = useState('');

  // Real-time countdown timer for active attendance code
  useEffect(() => {
    if (!codeExpiresAt) {
      setRemainingSeconds(0);
      return;
    }
    const updateCountdown = () => {
      const now = Date.now();
      const expiry = new Date(codeExpiresAt).getTime();
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setRemainingSeconds(diff);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [codeExpiresAt]);

  const isCodeActive = Boolean(activeSessionCode && codeExpiresAt && remainingSeconds > 0);
  const isCodeExpired = Boolean(activeSessionCode && codeExpiresAt && remainingSeconds <= 0);

  const formatCountdown = (totalSecs) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatTimeOnly = (dateInput) => {
    if (!dateInput) return '';
    try {
      const d = new Date(dateInput);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatLongDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length < 3) return dateStr;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return dateStr;
    return `${d} ${MONTH_NAMES[m - 1]} ${y}`;
  };

  const executeGenerateSessionCode = async (scheduleId, classDate = null, durationMinutes = selectedValidityDuration) => {
    const targetId = scheduleId || selectedAttendanceSchedule?.ScheduleID;
    const targetDate = classDate || selectedAttendanceDate;
    if (!targetId) return;
    setGeneratingCode(true);
    try {
      const res = await fetch('/api/trainer/attendance/generate-session-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduleId: targetId,
          classDate: targetDate,
          durationMinutes: durationMinutes || 10
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setActiveSessionCode(data.sessionCode);
        setCodeExpiresAt(data.expiresAt);
        setCodeDuration(data.durationMinutes || durationMinutes);
        const dateText = targetDate ? formatLongDate(targetDate) : '';
        setToast({
          type: 'success',
          msg: `New session code '${data.sessionCode}' generated (${data.durationMinutes || durationMinutes} mins validity)${dateText ? ' for ' + dateText : ''}!`
        });
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to generate code.' });
      }
    } catch (err) {
      setToast({ type: 'error', msg: 'Connection error.' });
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleGenerateSessionCode = (scheduleId, classDate = null) => {
    if (isCodeActive) {
      setConfirmRegenModalOpen(true);
    } else {
      executeGenerateSessionCode(scheduleId, classDate, selectedValidityDuration);
    }
  };

  // Toast notifications
  const [toast, setToast] = useState(null);

  // Calendar navigation state
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchAttendance = useCallback(async (scheduleId, dateStr = null) => {
    setLoadingAttendance(true);
    try {
      let url = `/api/trainer/attendance/${scheduleId}`;
      if (dateStr) {
        url += `?date=${encodeURIComponent(dateStr)}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setAttendanceList(data.data);
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to load attendance records.' });
      }
    } catch (err) {
      setToast({ type: 'error', msg: 'Failed to connect to attendance API.' });
    } finally {
      setLoadingAttendance(false);
    }
  }, []);

  const fetchSessionCode = useCallback(async (scheduleId, dateStr = null) => {
    if (!scheduleId) return;
    try {
      let url = `/api/trainer/attendance/session-code/${scheduleId}`;
      if (dateStr) {
        url += `?date=${encodeURIComponent(dateStr)}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setActiveSessionCode(data.sessionCode || null);
        setCodeExpiresAt(data.expiresAt || null);
        setCodeDuration(data.durationMinutes || 10);
      }
    } catch (err) {
      console.error('Failed to fetch session code:', err);
    }
  }, []);

  const handleSelectAttendanceDate = (dateStr) => {
    setSelectedAttendanceDate(dateStr);
    setActiveSessionCode(null);
    setCodeExpiresAt(null);
    setRemainingSeconds(0);
    if (selectedAttendanceSchedule) {
      fetchAttendance(selectedAttendanceSchedule.ScheduleID, dateStr);
      fetchSessionCode(selectedAttendanceSchedule.ScheduleID, dateStr);
    }
  };

  const fetchTrainerCourses = useCallback(async () => {
    const identifier = user?.email || user?.username;
    if (!identifier) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/trainer/courses/${encodeURIComponent(identifier)}`);
      const data = await res.json();
      if (data.status === 'success') {
        const rawList = Array.isArray(data.data) ? data.data : [];
        // Deduplicate any identical schedule entries for the same course, date range, and time
        const seen = new Set();
        const deduplicated = [];
        for (const item of rawList) {
          const key = `${item.CourseID || item.CourseName}_${item.StartDate}_${item.EndDate}_${item.StartTime || ''}`;
          if (!seen.has(key)) {
            seen.add(key);
            deduplicated.push(item);
          } else {
            const existingIdx = deduplicated.findIndex(
              x => `${x.CourseID || x.CourseName}_${x.StartDate}_${x.EndDate}_${x.StartTime || ''}` === key
            );
            if (existingIdx !== -1 && (item.EnrolledCount || 0) > (deduplicated[existingIdx].EnrolledCount || 0)) {
              deduplicated[existingIdx] = item;
            }
          }
        }
        setCourses(deduplicated);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch assigned courses.');
    } finally {
      setLoading(false);
    }
  }, [user?.email, user?.username, selectedAttendanceSchedule, fetchAttendance]);

  const fetchTrainerProfile = useCallback(async () => {
    const identifier = user?.email || user?.username;
    if (!identifier) return;
    try {
      const res = await fetch(`/api/profile/${encodeURIComponent(identifier)}`);
      const data = await res.json();
      if (data.status === 'success') {
        setTrainerProfile(data.profile);
      }
    } catch (err) {
      console.error('Failed to fetch trainer profile info:', err);
    }
  }, [user?.email, user?.username]);

  const [bioInput, setBioInput] = useState(user?.bio || '');
  const [certificationsInput, setCertificationsInput] = useState(user?.certifications || '');
  const [savingBio, setSavingBio] = useState(false);

  const fetchTrainerBioAndCerts = useCallback(async () => {
    const trainerIdentifier = user?.trainerId || user?.id || user?.email || user?.username;
    if (!trainerIdentifier) return;
    try {
      const res = await fetch(`/api/trainer/profile/${encodeURIComponent(trainerIdentifier)}`);
      const data = await res.json();
      if (data.status === 'success') {
        setBioInput(data.data.Bio || '');
        setCertificationsInput(data.data.Certifications || '');
      }
    } catch (err) {
      console.error(err);
    }
  }, [user?.trainerId, user?.id, user?.email, user?.username]);

  const handleSaveBioAndCerts = async () => {
    const trainerIdentifier = user?.trainerId || user?.id || user?.email || user?.username;
    if (!trainerIdentifier) return;
    setSavingBio(true);
    try {
      const res = await fetch(`/api/trainer/profile/${encodeURIComponent(trainerIdentifier)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bio: bioInput,
          certifications: certificationsInput
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: 'Bio & Certifications updated successfully!' });
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update profile.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setSavingBio(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchTrainerCourses();
      fetchTrainerProfile();
      fetchTrainerBioAndCerts();
    }
  }, [user, fetchTrainerBioAndCerts, fetchTrainerCourses, fetchTrainerProfile]);

  const handleRefreshDashboard = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchTrainerCourses(),
        fetchTrainerProfile(),
        fetchTrainerBioAndCerts(),
        fetchAttendanceSettings(),
        selectedAttendanceSchedule ? fetchAttendance(selectedAttendanceSchedule.ScheduleID, selectedAttendanceDate) : Promise.resolve(),
        selectedAttendanceSchedule ? fetchSessionCode(selectedAttendanceSchedule.ScheduleID, selectedAttendanceDate) : Promise.resolve()
      ]);
    } catch (err) {
      console.error('Refresh status error:', err);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const openRosterModal = async (schedule) => {
    setSelectedSchedule(schedule);
    setRosterModalOpen(true);
    setLoadingRoster(true);
    setRoster([]);
    try {
      const res = await fetch(`/api/trainer/schedule/${schedule.ScheduleID}/learners`);
      const data = await res.json();
      if (data.status === 'success') {
        setRoster(data.data);
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to load roster.' });
      }
    } catch (err) {
      setToast({ type: 'error', msg: 'Failed to connect to roster API.' });
    } finally {
      setLoadingRoster(false);
    }
  };

  const downloadCalendarInvite = (course) => {
    const trainerName = user?.fullName || user?.name || 'Staff Trainer';
    const title = `${course.CertificationBody} ${course.CourseName}`;
    const description = `Course: ${title}\\nType: ${course.Type}\\nTrainer: ${trainerName}\\nVenue: ${course.Venue || 'CCoE, Cyberjaya'}`;
    const location = course.Venue || 'CCoE, Cyberjaya';

    const startClean = (course.StartDate || '').replace(/-/g, '');
    const endClean = (course.EndDate || '').replace(/-/g, '');

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Cloudonuts CCoE//Trainer Schedule//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      `DTSTART;VALUE=DATE:${startClean}`,
      `DTEND;VALUE=DATE:${endClean}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `${course.CourseName.replace(/\s+/g, '_')}_Invite.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setToast({ type: 'success', msg: `Calendar invite (.ics) downloaded for ${course.CourseName}!` });
  };

  const downloadAllCalendarInvites = () => {
    if (!courses || courses.length === 0) {
      setToast({ type: 'error', msg: 'No assigned courses to export.' });
      return;
    }
    const trainerName = user?.fullName || user?.name || 'Staff Trainer';
    let eventsContent = [];
    courses.forEach(course => {
      const title = `${course.CertificationBody} ${course.CourseName}`;
      const description = `Course: ${title}\\nType: ${course.Type}\\nTrainer: ${trainerName}\\nVenue: ${course.Venue || 'CCoE, Cyberjaya'}`;
      const location = course.Venue || 'CCoE, Cyberjaya';
      const startClean = (course.StartDate || '').replace(/-/g, '');
      const endClean = (course.EndDate || '').replace(/-/g, '');

      eventsContent.push([
        'BEGIN:VEVENT',
        `SUMMARY:${title}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        `DTSTART;VALUE=DATE:${startClean}`,
        `DTEND;VALUE=DATE:${endClean}`,
        'STATUS:CONFIRMED',
        'END:VEVENT'
      ].join('\r\n'));
    });

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Cloudonuts CCoE//Trainer Teaching Schedule//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      ...eventsContent,
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `Trainer_Teaching_Schedule_All.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setToast({ type: 'success', msg: `Exported ${courses.length} course calendar invites (.ics)!` });
  };

  const handleFirstLoginPasswordReset = async (e) => {
    e.preventDefault();
    setFirstLoginError('');
    if (firstLoginPassword.length < 6) {
      setFirstLoginError('Password must be at least 6 characters long.');
      return;
    }
    if (firstLoginPassword !== firstLoginConfirmPassword) {
      setFirstLoginError('Passwords do not match.');
      return;
    }

    setFirstLoginSubmitting(true);
    try {
      const res = await fetch('/api/trainer/reset-first-login-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, username: user.username, newPassword: firstLoginPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: 'Password updated successfully! Welcome to your dashboard.' });
        setFirstLoginModalOpen(false);
        user.isFirstLogin = false;
      } else {
        setFirstLoginError(data.message || 'Failed to update password.');
      }
    } catch (err) {
      setFirstLoginError('Failed to connect to server.');
    } finally {
      setFirstLoginSubmitting(false);
    }
  };


  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!newEmail || !confirmEmail) {
      setEmailError('All email fields are required.');
      return;
    }
    if (newEmail !== confirmEmail) {
      setEmailError('Emails do not match.');
      return;
    }
    if (newEmail === user.email) {
      setEmailError('New email must be different from current email.');
      return;
    }

    setEmailSubmitting(true);
    try {
      const res = await fetch('/api/profile/email', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentEmail: user.email, newEmail })
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: 'Email updated successfully. Please log in again.' });
        setEmailModalOpen(false);
        setTimeout(() => {
          onLogout();
        }, 1500);
      } else {
        setEmailError(data.message || 'Failed to update email.');
      }
    } catch (err) {
      setEmailError('Failed to connect to server.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleMarkAttendance = async (attendanceId, newStatus) => {
    try {
      const res = await fetch('/api/trainer/attendance/mark', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attendanceId, status: newStatus })
      });
      const data = await res.json();
      if (res.ok) {
        setAttendanceList(prev => prev.map(item => item.AttendanceID === attendanceId ? {
          ...item,
          Status: newStatus,
          CheckInTime: newStatus === 'Present' ? (item.CheckInTime || new Date().toLocaleString()) : null
        } : item));
        setToast({ type: 'success', msg: `Attendance marked as ${newStatus}.` });
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update attendance.' });
      }
    } catch (err) {
      setToast({ type: 'error', msg: 'Connection error.' });
    }
  };

  const handleCodeCheckIn = async (e) => {
    e.preventDefault();
    if (!checkInCodeInput.trim()) return;
    setCheckInSubmitting(true);
    setCheckInMsg(null);
    try {
      const res = await fetch('/api/trainer/attendance/checkin-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          searchQuery: checkInCodeInput.trim(),
          checkInCode: checkInCodeInput.trim(),
          scheduleId: selectedAttendanceSchedule?.ScheduleID
        })
      });
      const data = await res.json();
      if (res.ok) {
        setCheckInMsg({ type: 'success', text: data.message });
        setCheckInCodeInput('');
        if (selectedAttendanceSchedule) {
          fetchAttendance(selectedAttendanceSchedule.ScheduleID);
        }
      } else {
        setCheckInMsg({ type: 'error', text: data.message || 'Invalid learner username or email.' });
      }
    } catch (err) {
      setCheckInMsg({ type: 'error', text: 'Connection error. Could not verify learner.' });
    } finally {
      setCheckInSubmitting(false);
    }
  };

  const getDatesInRange = (startDateStr, endDateStr) => {
    const dates = [];
    const [sy, sm, sd] = startDateStr.split('-').map(Number);
    const [ey, em, ed] = endDateStr.split('-').map(Number);

    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const cur = new Date(start);

    while (cur <= end) {
      dates.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`);
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  // Helper for toggling course accordion item expansion
  const toggleCourseExpand = (scheduleId) => {
    setExpandedCourses(prev => ({
      ...prev,
      [scheduleId]: !prev[scheduleId]
    }));
  };

  // Helper date comparisons for timeframe sorting/filtering
  const isCoursePast = useCallback((course) => {
    const endDateStr = course.EndDate || course.StartDate;
    if (!endDateStr) return false;
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const currentMonthKey = todayStr.substring(0, 7);
    const courseMonthKey = endDateStr.substring(0, 7);

    if (courseMonthKey < currentMonthKey) return true;
    if (courseMonthKey > currentMonthKey) return false;
    return endDateStr < todayStr;
  }, []);

  const isCourseUpcoming = useCallback((course) => !isCoursePast(course), [isCoursePast]);

  // Filter & calculate timeframe counts
  const upcomingCourses = courses.filter(isCourseUpcoming);
  const pastCourses = courses.filter(isCoursePast);

  const filteredCourses = courses.filter(c => timeframeTab === 'past' ? isCoursePast(c) : isCourseUpcoming(c));

  // Group filtered courses by month
  const groupCoursesByMonth = (courseList) => {
    const map = {};
    courseList.forEach(c => {
      const dStr = c.StartDate || c.EndDate || '';
      let year = 2026;
      let monthIdx = 8;
      if (dStr && dStr.includes('-')) {
        const p = dStr.split('-').map(Number);
        if (p.length >= 2 && !isNaN(p[0]) && !isNaN(p[1])) {
          year = p[0];
          monthIdx = p[1] - 1;
        }
      }
      const monthKey = `${year}-${String(monthIdx + 1).padStart(2, '0')}`;
      const shortMonth = MONTH_NAMES[monthIdx] ? MONTH_NAMES[monthIdx].substring(0, 3).toUpperCase() : 'UNK';
      const fullLabel = `${MONTH_NAMES[monthIdx] || 'Unknown'} ${year}`;

      if (!map[monthKey]) {
        map[monthKey] = {
          key: monthKey,
          shortMonth,
          fullLabel,
          year,
          monthIdx,
          courses: []
        };
      }
      // Defensive check to avoid duplicate rows within the same month box
      const courseKey = `${c.CourseID || c.CourseName}_${c.StartDate}_${c.EndDate}_${c.StartTime || ''}`;
      const existing = map[monthKey].courses.find(
        x => `${x.CourseID || x.CourseName}_${x.StartDate}_${x.EndDate}_${x.StartTime || ''}` === courseKey
      );
      if (!existing) {
        map[monthKey].courses.push(c);
      } else if ((c.EnrolledCount || 0) > (existing.EnrolledCount || 0)) {
        const idx = map[monthKey].courses.indexOf(existing);
        map[monthKey].courses[idx] = c;
      }
    });

    const sortedKeys = Object.keys(map).sort((a, b) => {
      return timeframeTab === 'past' ? b.localeCompare(a) : a.localeCompare(b);
    });

    return sortedKeys.map(k => map[k]);
  };

  const groupedMonths = groupCoursesByMonth(filteredCourses);

  const parseCourseSegments = (course) => {
    if (!course) return null;
    const raw = course.SessionSegments || course.sessionSegments;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch (e) {
      return null;
    }
  };

  const getCourseActiveDates = (course) => {
    if (!course) return [];
    const segs = parseCourseSegments(course);
    if (segs && segs.length > 0) {
      const allDates = [];
      segs.forEach(seg => {
        if (seg.startDate && seg.endDate) {
          const partDates = getDatesInRange(seg.startDate, seg.endDate);
          partDates.forEach(d => {
            if (!allDates.includes(d)) allDates.push(d);
          });
        }
      });
      if (allDates.length > 0) return allDates;
    }
    if (course.StartDate && course.EndDate) {
      return getDatesInRange(course.StartDate, course.EndDate);
    }
    return [];
  };

  // Pre-calculate statistics
  const totalStudents = courses.reduce((sum, c) => sum + (c.EnrolledCount || 0), 0);
  const totalDaysTaught = courses.reduce((sum, c) => sum + getCourseActiveDates(c).length, 0);

  // Parse details for Calendar Tab
  const calendarWeeks = getCalendarWeeks(viewYear, viewMonth);
  const calendarEvents = courses.map(c => {
    const dates = getCourseActiveDates(c);
    const accentColor = (c.Type || '').toLowerCase().includes('exam') ? '#10b981' : '#4f46e5';
    return {
      ...c,
      dates,
      accentColor
    };
  });

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return 'N/A';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const [lastLoginTime] = useState(() => {
    const now = new Date();
    return now.toLocaleDateString('en-GB') + ' ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  });

  const formatDateTimeString = (dateInput) => {
    if (!dateInput) return lastLoginTime;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return lastLoginTime;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day}/${month}/${year} ${timeStr}`;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '22/06/2026';

  // Attendance metrics
  const filteredAttendance = attendanceList.filter(item => {
    const matchesFilter = attendanceFilter === 'all' || item.Status === attendanceFilter;
    const query = attendeeSearchQuery.trim().toLowerCase();
    const matchesSearch = !query || (
      (item.FullName && item.FullName.toLowerCase().includes(query)) ||
      (item.Email && item.Email.toLowerCase().includes(query)) ||
      (item.Username && item.Username.toLowerCase().includes(query)) ||
      (item.OrganisationName && item.OrganisationName.toLowerCase().includes(query))
    );
    return matchesFilter && matchesSearch;
  });
  const presentCount = attendanceList.filter(i => i.Status === 'Present').length;
  const absentCount = attendanceList.filter(i => i.Status === 'Absent').length;
  const attendanceRate = attendanceList.length > 0 ? Math.round((presentCount / attendanceList.length) * 100) : 0;

  return (
    <div className="profile-page-layout">
      <Sidebar
        activeView={
          activeTab === 'calendar' ? 'trainerCalendar' :
            activeTab === 'profile' ? 'trainerProfile' :
              'account'
        }
        onSwitchView={(v) => {
          if (v === 'trainerCalendar') {
            setActiveTab('calendar');
            if (onSwitchView) onSwitchView('trainerCalendar');
          } else if (v === 'trainerProfile') {
            setActiveTab('profile');
            if (onSwitchView) onSwitchView('trainerProfile');
          } else if (v === 'account') {
            setActiveTab('overview');
            if (onSwitchView) onSwitchView('account');
          } else {
            onSwitchView(v);
          }
        }}
        onLogout={onLogout}
        user={user}
      />

      <div className="profile-main-content">
        <header className="profile-top-bar trainer-top-bar">
          <div className="profile-top-bar-left">
            <SvgIcon name={activeTab === 'profile' ? 'user' : (activeTab === 'calendar' ? 'calendar' : activeTab === 'attendance' ? 'checkSquare' : 'grid')} size={22} />
            <h1>{activeTab === 'profile' ? 'Profile' : (activeTab === 'calendar' ? 'Training Calendar' : activeTab === 'attendance' ? 'Attendance Management' : 'Dashboard')}</h1>
          </div>
          <div className="profile-top-bar-right">
            {activeTab !== 'profile' && (
              <button
                className="td-refresh-btn"
                onClick={handleRefreshDashboard}
                disabled={isRefreshing}
                title="Refresh dashboard data and live status"
                aria-label="Refresh dashboard data and live status"
              >
                <SvgIcon name="refresh" size={15} className={isRefreshing ? 'spin-icon' : ''} />
                <span className="td-refresh-text-full">{isRefreshing ? 'Refreshing...' : 'Refresh Status'}</span>
                <span className="td-refresh-text-short">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            )}

            <div className="profile-top-bar-user" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="profile-top-bar-empty-avatar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              </div>
              <span className="profile-top-bar-username">{trainerProfile?.fullName || user?.fullName}</span>
            </div>
          </div>
        </header>

        <div className="profile-content-body">
          {toast && (
            <div className={`td-toast ${toast.type}`}>
              <span>{toast.msg}</span>
            </div>
          )}

          {activeTab === 'overview' && (
            <>
              <NotificationCentre user={user} />
              <div className="profile-page-header">

                <h2>Welcome back, {trainerProfile?.fullName || user?.fullName}!</h2>
              </div>

              {/* Quick Stats Grid */}
              <div className="td-stats-grid">
                <div className="td-stat-card card-blue">
                  <div className="td-stat-icon">
                    <SvgIcon name="book" size={24} />
                  </div>
                  <div className="td-stat-info">
                    <h3>{courses.length}</h3>
                    <p>Assigned Courses</p>
                  </div>
                </div>

                <div className="td-stat-card card-indigo">
                  <div className="td-stat-icon">
                    <SvgIcon name="users" size={24} />
                  </div>
                  <div className="td-stat-info">
                    <h3>{totalStudents}</h3>
                    <p>Total Enrolled Learners</p>
                  </div>
                </div>

                <div className="td-stat-card card-emerald">
                  <div className="td-stat-icon">
                    <SvgIcon name="calendar" size={24} />
                  </div>
                  <div className="td-stat-info">
                    <h3>{totalDaysTaught}</h3>
                    <p>Teaching Days</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'profile' && (
            <div className="profile-page-header">
              <h2>Trainer Profile</h2>
              <p>View and manage your personal and professional information.</p>
            </div>
          )}

          {/* Loading / Error States */}
          {loading ? (
            <div className="profile-loading">
              <div className="profile-loading-spinner" />
              <p>Loading trainer dashboard...</p>
            </div>
          ) : error ? (
            <div className="td-error-state">
              <SvgIcon name="alertCircle" size={48} />
              <p>Error: {error}</p>
            </div>
          ) : (
            <>
              {/* TAB 1: OVERVIEW */}
              {activeTab === 'overview' && (
                <div className="td-tab-content">
                  <div className="td-section-card">
                    {/* Timeframe Filter Tabs (Current & Upcoming / Past Sessions) */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
                      <div className="modal-timeframe-tabs">
                        <button
                          type="button"
                          className={`modal-timeframe-tab ${timeframeTab === 'upcoming' ? 'active' : ''}`}
                          onClick={() => setTimeframeTab('upcoming')}
                        >
                          Current & Upcoming ({upcomingCourses.length})
                        </button>
                        <button
                          type="button"
                          className={`modal-timeframe-tab ${timeframeTab === 'past' ? 'active' : ''}`}
                          onClick={() => setTimeframeTab('past')}
                        >
                          Past Sessions ({pastCourses.length})
                        </button>
                      </div>
                    </div>

                    <div className="td-card-header" style={{ flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div>
                        <h3>Assigned Teaching Schedule</h3>
                        <p className="td-card-subtitle">Manage your assigned training courses and export calendar invitations</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <div className="cc-view-toggle-bar" style={{ display: 'inline-flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                          <button
                            className={`cc-view-toggle-btn ${overviewViewMode === 'grouped' ? 'active' : ''}`}
                            onClick={() => setOverviewViewMode('grouped')}
                            style={{
                              padding: '0.35rem 0.75rem',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: overviewViewMode === 'grouped' ? '#ffffff' : 'transparent',
                              color: overviewViewMode === 'grouped' ? '#1e293b' : '#64748b',
                              boxShadow: overviewViewMode === 'grouped' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            Grouped View
                          </button>
                          <button
                            className={`cc-view-toggle-btn ${overviewViewMode === 'table' ? 'active' : ''}`}
                            onClick={() => setOverviewViewMode('table')}
                            style={{
                              padding: '0.35rem 0.75rem',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: overviewViewMode === 'table' ? '#ffffff' : 'transparent',
                              color: overviewViewMode === 'table' ? '#1e293b' : '#64748b',
                              boxShadow: overviewViewMode === 'table' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            Table View
                          </button>
                          <button
                            className={`cc-view-toggle-btn ${overviewViewMode === 'cards' ? 'active' : ''}`}
                            onClick={() => setOverviewViewMode('cards')}
                            style={{
                              padding: '0.35rem 0.75rem',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: overviewViewMode === 'cards' ? '#ffffff' : 'transparent',
                              color: overviewViewMode === 'cards' ? '#1e293b' : '#64748b',
                              boxShadow: overviewViewMode === 'cards' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            Card View
                          </button>
                        </div>
                        <span className="td-badge">{filteredCourses.length} Courses Assigned</span>
                      </div>
                    </div>

                    {filteredCourses.length === 0 ? (
                      <div className="td-empty-state">
                        <SvgIcon name="info" size={32} />
                        <p>
                          {timeframeTab === 'upcoming'
                            ? 'You currently do not have any current or upcoming courses assigned.'
                            : 'No past course sessions found.'}
                        </p>
                      </div>
                    ) : overviewViewMode === 'grouped' ? (
                      <div className="modal-monthly-scroll-container">
                        {groupedMonths.map((m) => {
                          const monthLabel = m.fullLabel;
                          const shortMonth = m.shortMonth;
                          const courseCount = m.courses.length;
                          const totalReg = m.courses.reduce((sum, c) => sum + (c.EnrolledCount || 0), 0);

                          return (
                            <div key={m.key} className="modal-month-box">
                              {/* Month Box Header */}
                              <div className="modal-month-box-header">
                                <div className="modal-month-header-left">
                                  <span className="modal-month-pill">
                                    <SvgIcon name="calendar" size={14} />
                                    <span>{shortMonth}</span>
                                  </span>
                                  <span className="modal-month-fullname">{monthLabel}</span>
                                </div>
                                <div className="modal-month-header-right">
                                  <span className="modal-month-summary-badge">
                                    {courseCount} {courseCount === 1 ? 'Course' : 'Courses'} • {totalReg} {totalReg === 1 ? 'Registration' : 'Registrations'}
                                  </span>
                                </div>
                              </div>

                              {/* Month Box Body - Courses within this month */}
                              <div className="modal-month-box-body">
                                {m.courses.map((course) => {
                                  const isExpanded = !!expandedCourses[course.ScheduleID];
                                  const segs = parseCourseSegments(course);
                                  const hasMultiSegs = segs && segs.length > 1;
                                  const datesRange = hasMultiSegs
                                    ? segs.map((s, i) => `Pt ${i + 1}: ${formatDateDisplay(s.startDate)} - ${formatDateDisplay(s.endDate)}`).join(', ')
                                    : `${formatDateDisplay(course.StartDate)} - ${formatDateDisplay(course.EndDate)}`;
                                  const regCount = course.EnrolledCount || 0;

                                  return (
                                    <div key={course.ScheduleID} className="modal-course-accordion-item">
                                      <div
                                        className="modal-course-accordion-header"
                                        onClick={() => toggleCourseExpand(course.ScheduleID)}
                                      >
                                        <div className="modal-course-header-left">
                                          <div className="modal-course-title-block" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', gap: '2px' }}>
                                            <span className="modal-course-title" style={{ textAlign: 'left' }}>{course.CourseName}</span>
                                            <span className="modal-course-schedule-tag" style={{ fontSize: '0.825rem', color: '#64748b', fontWeight: '500', textAlign: 'left' }}>
                                              🗓️ {datesRange} ({course.StartTime})
                                            </span>
                                          </div>
                                          <span
                                            className="modal-course-badge"
                                            style={{
                                              backgroundColor: '#e0e7ff',
                                              color: '#4338ca',
                                              fontWeight: '700',
                                              padding: '3px 10px',
                                              borderRadius: '12px',
                                              fontSize: '0.75rem'
                                            }}
                                          >
                                            {regCount} {regCount === 1 ? 'Registration' : 'Registrations'}
                                          </span>
                                        </div>
                                        <div className="modal-course-header-right">
                                          <span className="accordion-plus-minus">
                                            {isExpanded ? '–' : '+'}
                                          </span>
                                        </div>
                                      </div>

                                      {isExpanded && (
                                        <div className="modal-course-accordion-body" style={{ padding: '1.25rem', background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                                          <div className="td-card-details-box" style={{ marginBottom: '1rem' }}>
                                            <div className="td-card-detail-item">
                                              <span className="td-cb-text" style={{ fontSize: '0.8rem', fontWeight: '700', color: '#64748b' }}>{course.CertificationBody}</span>
                                              <span className={`td-type-tag ${course.Type.toLowerCase().includes('exam') ? 'tag-exam' : 'tag-training'}`}>
                                                {course.Type}
                                              </span>
                                            </div>

                                            {hasMultiSegs ? (
                                              <div className="td-card-detail-item" style={{ alignItems: 'flex-start' }}>
                                                <SvgIcon name="calendar" size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                  {segs.map((s, idx) => (
                                                    <span key={idx} style={{ fontSize: '0.825rem', color: '#1e293b' }}>
                                                      <strong style={{ color: '#4f46e5' }}>Part {idx + 1}:</strong> {formatDateDisplay(s.startDate)} - {formatDateDisplay(s.endDate)} <span style={{ color: '#64748b', fontSize: '0.78rem' }}>({s.startTime || course.StartTime} - {s.endTime || course.EndTime})</span>
                                                    </span>
                                                  ))}
                                                  <span style={{ fontSize: '0.75rem', color: '#047857', fontWeight: 600 }}>
                                                    Total: {getCourseActiveDates(course).length} Training Days
                                                  </span>
                                                </div>
                                              </div>
                                            ) : (
                                              <>
                                                <div className="td-card-detail-item">
                                                  <SvgIcon name="calendar" size={14} />
                                                  <span>{datesRange}</span>
                                                </div>
                                                <div className="td-card-detail-item">
                                                  <SvgIcon name="clock" size={14} />
                                                  <span>{course.StartTime} - {course.EndTime}</span>
                                                </div>
                                              </>
                                            )}
                                            <div className="td-card-detail-item">
                                              <SvgIcon name="mapPin" size={14} />
                                              <span>{course.Venue || (course.CourseName.toLowerCase().includes('online') ? 'Online' : 'CCoE, Cyberjaya')}</span>
                                            </div>
                                            <div className="td-card-detail-item enrolled-item">
                                              <SvgIcon name="users" size={14} />
                                              <span>Enrolled Learners: <strong style={{ color: '#4f46e5' }}>{course.EnrolledCount}</strong></span>
                                            </div>
                                          </div>

                                          <div className="td-card-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <button
                                              className="td-action-btn"
                                              onClick={(e) => { e.stopPropagation(); openRosterModal(course); }}
                                              title="View Learner Class Roster"
                                            >
                                              <SvgIcon name="users" size={14} /> Class Roster
                                            </button>

                                            <button
                                              className="td-action-btn td-btn-attendance"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedAttendanceSchedule(course);
                                                setSelectedAttendanceDate(null);
                                                setActiveTab('attendance');
                                              }}
                                              title="Track Learner Attendance"
                                            >
                                              <SvgIcon name="checkSquare" size={14} /> Attendance
                                            </button>

                                            <AddToCalendarDropdown
                                              course={course}
                                              user={user}
                                              onToast={setToast}
                                              variant="compact"
                                              label="Add to Calendar"
                                              align="right"
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : overviewViewMode === 'table' ? (
                      <div className="td-table-responsive">
                        <table className="td-table">
                          <thead>
                            <tr>
                              <th>Course / Certification Body</th>
                              <th>Dates</th>
                              <th>Time</th>
                              <th>Venue</th>
                              <th>Enrolled</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCourses.map(course => (
                              <tr key={course.ScheduleID}>
                                <td>
                                  <div className="td-course-td-info">
                                    <span className="td-cb-text">{course.CertificationBody}</span>
                                    <span className="td-cn-text">{course.CourseName}</span>
                                    <span className={`td-type-tag ${course.Type.toLowerCase().includes('exam') ? 'tag-exam' : 'tag-training'}`}>
                                      {course.Type}
                                    </span>
                                  </div>
                                </td>
                                <td className="font-medium">
                                  {(() => {
                                    const segs = parseCourseSegments(course);
                                    if (segs && segs.length > 1) {
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                          {segs.map((s, idx) => (
                                            <span key={idx} style={{ fontSize: '0.78rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                              <strong style={{ color: '#4f46e5', marginRight: '4px' }}>Pt {idx + 1}:</strong>
                                              {formatDateDisplay(s.startDate)} - {formatDateDisplay(s.endDate)}
                                            </span>
                                          ))}
                                          <span style={{ fontSize: '0.7rem', color: '#047857', fontWeight: 600 }}>
                                            {getCourseActiveDates(course).length} Days Total
                                          </span>
                                        </div>
                                      );
                                    }
                                    return `${formatDateDisplay(course.StartDate)} - ${formatDateDisplay(course.EndDate)}`;
                                  })()}
                                </td>
                                <td>
                                  <span className="td-time-display">
                                    <SvgIcon name="clock" size={12} /> {course.StartTime} - {course.EndTime}
                                  </span>
                                </td>
                                <td>
                                  <span className="td-venue-display">
                                    <SvgIcon name="mapPin" size={12} /> {course.Venue || (course.CourseName.toLowerCase().includes('online') ? 'Online' : 'CCoE, Cyberjaya')}
                                  </span>
                                </td>
                                <td className="text-center font-bold text-indigo-600">
                                  {course.EnrolledCount}
                                </td>
                                <td>
                                  <div className="td-action-group">
                                    <button
                                      className="td-action-btn"
                                      onClick={() => openRosterModal(course)}
                                      title="View Learner Class Roster"
                                    >
                                      <SvgIcon name="users" size={14} /> Class Roster
                                    </button>

                                    <button
                                      className="td-action-btn td-btn-attendance"
                                      onClick={() => {
                                        setSelectedAttendanceSchedule(course);
                                        setSelectedAttendanceDate(null);
                                        setActiveTab('attendance');
                                      }}
                                      title="Track Learner Attendance"
                                    >
                                      <SvgIcon name="checkSquare" size={14} /> Attendance
                                    </button>

                                    <AddToCalendarDropdown
                                      course={course}
                                      user={user}
                                      onToast={setToast}
                                      variant="compact"
                                      label="Calendar"
                                      align="right"
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="td-courses-card-grid">
                        {filteredCourses.map(course => {
                          const segs = parseCourseSegments(course);
                          const hasMultiSegs = segs && segs.length > 1;

                          return (
                            <div key={course.ScheduleID} className="td-course-card">
                              <div className="td-card-head">
                                <span className="td-cb-text">{course.CertificationBody}</span>
                                <span className={`td-type-tag ${course.Type.toLowerCase().includes('exam') ? 'tag-exam' : 'tag-training'}`}>
                                  {course.Type}
                                </span>
                              </div>

                              <h4 className="td-card-course-title">{course.CourseName}</h4>

                              <div className="td-card-details-box">
                                {hasMultiSegs ? (
                                  <div className="td-card-detail-item" style={{ alignItems: 'flex-start' }}>
                                    <SvgIcon name="calendar" size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      {segs.map((s, idx) => (
                                        <span key={idx} style={{ fontSize: '0.8rem', color: '#1e293b' }}>
                                          <strong style={{ color: '#4f46e5' }}>Pt {idx + 1}:</strong> {formatDateDisplay(s.startDate)} - {formatDateDisplay(s.endDate)}
                                        </span>
                                      ))}
                                      <span style={{ fontSize: '0.72rem', color: '#047857', fontWeight: 600 }}>
                                        {getCourseActiveDates(course).length} Days Total
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="td-card-detail-item">
                                    <SvgIcon name="calendar" size={14} />
                                    <span>{formatDateDisplay(course.StartDate)} - {formatDateDisplay(course.EndDate)}</span>
                                  </div>
                                )}

                                <div className="td-card-detail-item">
                                  <SvgIcon name="clock" size={14} />
                                  <span>{course.StartTime} - {course.EndTime}</span>
                                </div>

                                <div className="td-card-detail-item">
                                  <SvgIcon name="mapPin" size={14} />
                                  <span>{course.Venue || (course.CourseName.toLowerCase().includes('online') ? 'Online' : 'CCoE, Cyberjaya')}</span>
                                </div>

                              <div className="td-card-detail-item enrolled-item">
                                <SvgIcon name="users" size={14} />
                                <span>Enrolled Learners: <strong style={{ color: '#4f46e5' }}>{course.EnrolledCount}</strong></span>
                              </div>
                            </div>

                            <div className="td-card-actions">
                              <button
                                className="td-action-btn"
                                onClick={() => openRosterModal(course)}
                                title="View Learner Class Roster"
                              >
                                <SvgIcon name="users" size={14} /> Class Roster
                              </button>

                              <button
                                className="td-action-btn td-btn-attendance"
                                onClick={() => {
                                  setSelectedAttendanceSchedule(course);
                                  setSelectedAttendanceDate(null);
                                  setActiveTab('attendance');
                                }}
                                title="Track Learner Attendance"
                              >
                                <SvgIcon name="checkSquare" size={14} /> Attendance
                              </button>

                              <AddToCalendarDropdown
                                course={course}
                                user={user}
                                onToast={setToast}
                                variant="compact"
                                label="Add to Calendar"
                                align="right"
                              />
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 2: MY CALENDAR */}
              {activeTab === 'calendar' && (
                <div className="td-tab-content">
                  <div className="td-section-card">
                    <div className="cc-calendar-header" style={{ marginBottom: '1.5rem' }}>
                      <div className="cc-calendar-header-left">
                        <h2 className="cc-calendar-title" style={{ fontSize: '1.5rem' }}>
                          {MONTH_NAMES[viewMonth]} {viewYear}
                        </h2>
                        <span className="cc-calendar-subtitle">Your visual teaching schedule & calendar invites</span>
                      </div>
                      <div className="cc-calendar-header-right">
                        <button
                          className="td-export-schedule-btn"
                          onClick={downloadAllCalendarInvites}
                          title="Export all assigned course schedule calendar invitations as .ics"
                        >
                          <SvgIcon name="download" size={14} /> <span>Export All Schedule (.ics)</span>
                        </button>
                        <div className="cc-month-nav-group td-calendar-nav-group">
                          <button className="cc-nav-btn" onClick={prevMonth} aria-label="Previous month" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                            &lt;
                          </button>
                          <button
                            className="cc-today-btn"
                            onClick={() => { setViewMonth(today.getMonth()); setViewYear(today.getFullYear()); }}
                          >
                            Today
                          </button>
                          <button className="cc-nav-btn" onClick={nextMonth} aria-label="Next month" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                            &gt;
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="cc-calendar-wrap">
                      <div className="cc-day-headers">
                        {DAY_NAMES.map(d => (
                          <div key={d} className="cc-day-header">{d}</div>
                        ))}
                      </div>

                      <div className="cc-weeks-container">
                        {calendarWeeks.map((weekDates, wIdx) => {
                          const weekSegments = [];
                          calendarEvents.forEach(evt => {
                            const datesInWeek = evt.dates.filter(d => weekDates.includes(d));
                            if (datesInWeek.length === 0) return;

                            const colIndices = datesInWeek.map(d => weekDates.indexOf(d));
                            const colStart = Math.min(...colIndices);
                            const colEnd = Math.max(...colIndices);
                            const span = colEnd - colStart + 1;

                            weekSegments.push({
                              event: evt,
                              colStart,
                              colEnd,
                              span
                            });
                          });

                          weekSegments.sort((a, b) => b.span - a.span || a.colStart - b.colStart);
                          const rowOccupied = [];
                          weekSegments.forEach(seg => {
                            let row = 0;
                            while (true) {
                              if (!rowOccupied[row]) rowOccupied[row] = Array(7).fill(false);

                              let free = true;
                              for (let c = seg.colStart; c <= seg.colEnd; c++) {
                                if (rowOccupied[row][c]) {
                                  free = false;
                                  break;
                                }
                              }

                              if (free) {
                                for (let c = seg.colStart; c <= seg.colEnd; c++) {
                                  rowOccupied[row][c] = true;
                                }
                                seg.row = row;
                                break;
                              }
                              row++;
                            }
                          });

                          const numRows = rowOccupied.length || 1;

                          return (
                            <div key={wIdx} className="cc-week-row" style={{ minHeight: `${80 + numRows * 26}px` }}>
                              <div className="cc-week-bg">
                                {weekDates.map((dateStr, dIdx) => {
                                  const [, , day] = dateStr.split('-').map(Number);
                                  const m = Number(dateStr.split('-')[1]) - 1;
                                  const isCurrentMonth = m === viewMonth;
                                  const isToday =
                                    day === today.getDate() &&
                                    m === today.getMonth() &&
                                    viewYear === today.getFullYear();

                                  return (
                                    <div
                                      key={dIdx}
                                      className={`cc-cell-bg ${isCurrentMonth ? '' : 'cc-cell-other-month'} ${isToday ? 'cc-cell-today' : ''}`}
                                    >
                                      <span className={`cc-day-num ${isToday ? 'cc-day-num-today' : ''}`}>
                                        {day}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>

                              <div className="cc-week-events-grid" style={{ gridTemplateRows: `repeat(${numRows}, auto)` }}>
                                {weekSegments.map((seg, sIdx) => {
                                  return (
                                    <div
                                      key={sIdx}
                                      className="cc-event-bar"
                                      style={{
                                        gridColumn: `${seg.colStart + 1} / ${seg.colEnd + 2}`,
                                        gridRow: `${seg.row + 1}`,
                                        borderLeftColor: seg.event.accentColor,
                                        background: `${seg.event.accentColor}18`,
                                        color: seg.event.accentColor
                                      }}
                                      onClick={() => openRosterModal(seg.event)}
                                      title="Click to view Class Roster"
                                    >
                                      <span className="cc-event-dot" style={{ background: seg.event.accentColor }} />
                                      <span className="cc-event-name" style={{ color: seg.event.accentColor, fontWeight: 'bold' }}>
                                        {seg.event.CertificationBody} {seg.event.CourseName}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ATTENDANCE MANAGEMENT (Navigated from Course Overview) */}
              {activeTab === 'attendance' && (
                <div className="td-tab-content">
                  {(() => {
                    const currentSchedule = selectedAttendanceSchedule || (courses.length > 0 ? courses[0] : null);
                    if (!currentSchedule) {
                      return (
                        <div className="td-section-card">
                          <div className="td-empty-state">
                            <SvgIcon name="info" size={32} />
                            <p>No assigned course selected. Please select a course from Course Overview.</p>
                          </div>
                        </div>
                      );
                    }

                    const scheduledDates = getCourseActiveDates(currentSchedule);
                    const currentSegs = parseCourseSegments(currentSchedule);
                    const hasMultiSegs = currentSegs && currentSegs.length > 1;

                    // STEP 1: DATE SELECTION STEP (If no date is selected yet)
                    if (!selectedAttendanceDate) {
                      const startParts = (currentSchedule.StartDate || '').split('-').map(Number);
                      const startYear = startParts[0] || 2026;
                      const startMonth = (startParts[1] || 9) - 1; // 0-indexed
                      const calWeeks = getCalendarWeeks(startYear, startMonth);

                      return (
                        <>
                          <div className="td-att-course-info-banner" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', padding: '1rem 1.25rem', borderRadius: '14px', marginBottom: '0.85rem' }}>
                            <div className="td-att-banner-header" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', paddingBottom: '0.6rem', marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div className="td-att-banner-title">
                                <span className="td-cb-text" style={{ color: '#a5b4fc', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {currentSchedule.CertificationBody}
                                </span>
                                <h2 style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: '800', margin: '0.1rem 0 0 0' }}>
                                  {currentSchedule.CourseName}
                                </h2>
                              </div>
                              <button
                                className="td-att-back-btn"
                                onClick={() => setActiveTab('overview')}
                                title="Return to Course Overview"
                                style={{
                                  background: 'rgba(255, 255, 255, 0.12)',
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.25)',
                                  padding: '0.35rem 0.8rem',
                                  borderRadius: '6px',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.4rem'
                                }}
                              >
                                &larr; Back to Course Overview
                              </button>
                            </div>

                            <div className="td-att-info-grid">
                              <div className="td-att-info-item">
                                <div className="td-att-info-icon">
                                  <SvgIcon name="calendar" size={15} />
                                </div>
                                <div>
                                  <span className="td-att-info-label">Schedule Dates</span>
                                  {hasMultiSegs ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                      {currentSegs.map((s, idx) => (
                                        <span key={idx} style={{ color: '#e0e7ff', fontSize: '0.8rem', fontWeight: '600' }}>
                                          Part {idx + 1}: {formatDateDisplay(s.startDate)} - {formatDateDisplay(s.endDate)}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="td-att-info-val">
                                      {formatDateDisplay(currentSchedule.StartDate)} - {formatDateDisplay(currentSchedule.EndDate)}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="td-att-info-item">
                                <div className="td-att-info-icon">
                                  <SvgIcon name="clock" size={15} />
                                </div>
                                <div>
                                  <span className="td-att-info-label">Class Time</span>
                                  <span className="td-att-info-val">
                                    {currentSchedule.StartTime} - {currentSchedule.EndTime}
                                  </span>
                                </div>
                              </div>

                              <div className="td-att-info-item">
                                <div className="td-att-info-icon">
                                  <SvgIcon name="mapPin" size={15} />
                                </div>
                                <div>
                                  <span className="td-att-info-label">Venue</span>
                                  <span className="td-att-info-val">
                                    {currentSchedule.Venue || (currentSchedule.CourseName.toLowerCase().includes('online') ? 'Online' : 'CCoE, Cyberjaya')}
                                  </span>
                                </div>
                              </div>

                              <div className="td-att-info-item">
                                <div className="td-att-info-icon">
                                  <SvgIcon name="users" size={15} />
                                </div>
                                <div>
                                  <span className="td-att-info-label">Enrolled Learners</span>
                                  <span className="td-att-info-val">{currentSchedule.EnrolledCount}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* DATE SELECTION STEP CARD */}
                          <div className="td-date-select-card">
                            <div className="td-date-select-header">
                              <div className="td-date-select-header-icon">
                                <SvgIcon name="calendar" size={20} />
                              </div>
                              <div className="td-date-select-header-text">
                                <h3>Select Class Session Date</h3>
                                <p>Select a scheduled class date below to generate an attendance code and record learner check-ins.</p>
                              </div>
                            </div>

                            <div className="td-date-select-body-split">
                              {/* Monthly Calendar View */}
                              <div className="td-date-calendar-box">
                                <div className="td-date-calendar-month-title">
                                  <SvgIcon name="calendar" size={16} />
                                  <span>{MONTH_NAMES[startMonth]} {startYear}</span>
                                </div>

                                <div className="td-date-day-names">
                                  {DAY_NAMES.map(d => (
                                    <div key={d}>{d}</div>
                                  ))}
                                </div>

                                <div className="td-date-cells-grid">
                                  {calWeeks.flat().map((dateStr, idx) => {
                                    const [, mStr, dStr] = dateStr.split('-');
                                    const dayNum = parseInt(dStr, 10);
                                    const mIdx = parseInt(mStr, 10) - 1;
                                    const isScheduled = scheduledDates.includes(dateStr);
                                    const isCurrentMonth = mIdx === startMonth;

                                    if (isScheduled) {
                                      return (
                                        <button
                                          key={dateStr || idx}
                                          type="button"
                                          className="td-date-cell td-date-active"
                                          onClick={() => handleSelectAttendanceDate(dateStr)}
                                          title={`Select scheduled class date: ${formatLongDate(dateStr)}`}
                                        >
                                          <span className="td-date-cell-num">{dayNum}</span>
                                          <span className="td-date-cell-label">Class Day</span>
                                        </button>
                                      );
                                    }

                                    return (
                                      <div
                                        key={dateStr || idx}
                                        className="td-date-cell td-date-disabled"
                                        style={{ opacity: isCurrentMonth ? 0.35 : 0.15 }}
                                        title="No class scheduled on this date"
                                      >
                                        <span className="td-date-cell-num">{dayNum}</span>
                                        <span className="td-date-cell-disabled-lbl">No Class</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Quick Select Scheduled Class Date Cards */}
                              <div className="td-date-cards-section">
                                <div className="td-date-cards-header">
                                  Scheduled Class Dates ({scheduledDates.length} {scheduledDates.length === 1 ? 'Day' : 'Days'}{hasMultiSegs ? ` • ${currentSegs.length} Parts` : ''})
                                </div>
                                <div className="td-date-cards-grid">
                                  {scheduledDates.map((dateStr, idx) => {
                                    const longDate = formatLongDate(dateStr);
                                    const dayName = new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long' });
                                    let partBadge = null;
                                    if (hasMultiSegs) {
                                      for (let p = 0; p < currentSegs.length; p++) {
                                        if (dateStr >= currentSegs[p].startDate && dateStr <= currentSegs[p].endDate) {
                                          partBadge = `Part ${p + 1}`;
                                          break;
                                        }
                                      }
                                    }

                                    return (
                                      <div
                                        key={dateStr}
                                        className="td-date-card"
                                        onClick={() => handleSelectAttendanceDate(dateStr)}
                                      >
                                        <div className="td-date-card-left">
                                          <span className="td-date-card-badge">
                                            {partBadge ? `${partBadge} • ` : ''}Day {idx + 1} of {scheduledDates.length}
                                          </span>
                                          <span className="td-date-card-day">{dayName}</span>
                                          <span className="td-date-card-date">{longDate}</span>
                                        </div>
                                        <button type="button" className="td-date-card-btn">
                                          Select Date &rarr;
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    }

                    // STEP 2: LIVE SESSION CODE GENERATOR & ATTENDANCE ROSTER (When a date is selected)
                    return (
                      <>
                        {/* COMBINED UNIFIED COURSE INFO & LIVE SESSION CODE GENERATOR CARD */}
                        <div className="td-att-course-info-banner" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', padding: '1.75rem', borderRadius: '16px', marginBottom: '1.5rem' }}>
                          <div className="td-att-banner-header" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', paddingBottom: '1rem', marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                            <div className="td-att-banner-title">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                                <span className="td-cb-text" style={{ color: '#a5b4fc', fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {currentSchedule.CertificationBody}
                                </span>
                                <span className="td-selected-date-pill" style={{ background: '#e0e7ff', color: '#312e81', padding: '0.25rem 0.75rem', borderRadius: '20px', fontWeight: '800', fontSize: '0.825rem' }}>
                                  🗓️ Attendance – {formatLongDate(selectedAttendanceDate)}
                                </span>
                              </div>
                              <h2 style={{ color: '#ffffff', fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>
                                {currentSchedule.CourseName}
                              </h2>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button
                                className="td-att-back-btn"
                                onClick={() => setSelectedAttendanceDate(null)}
                                title="Select another class date"
                                style={{
                                  background: 'rgba(255, 255, 255, 0.15)',
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                  padding: '0.45rem 0.95rem',
                                  borderRadius: '8px',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.4rem'
                                }}
                              >
                                &larr; Back / Change Date
                              </button>
                              <button
                                className="td-att-back-btn"
                                onClick={() => setActiveTab('overview')}
                                title="Return to Course Overview"
                                style={{
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  padding: '0.45rem 0.95rem',
                                  borderRadius: '8px',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.4rem'
                                }}
                              >
                                Course Overview &rarr;
                              </button>
                            </div>
                          </div>

                          <div className="td-att-info-grid" style={{ marginBottom: '1.5rem' }}>
                            <div className="td-att-info-item">
                              <div className="td-att-info-icon">
                                <SvgIcon name="calendar" size={16} />
                              </div>
                              <div>
                                <span className="td-att-info-label">Attendance Date</span>
                                <span className="td-att-info-val" style={{ color: '#a5b4fc', fontWeight: '800' }}>
                                  {formatLongDate(selectedAttendanceDate)}
                                </span>
                              </div>
                            </div>

                            <div className="td-att-info-item">
                              <div className="td-att-info-icon">
                                <SvgIcon name="clock" size={16} />
                              </div>
                              <div>
                                <span className="td-att-info-label">Class Time</span>
                                <span className="td-att-info-val">
                                  {currentSchedule.StartTime} - {currentSchedule.EndTime}
                                </span>
                              </div>
                            </div>

                            <div className="td-att-info-item">
                              <div className="td-att-info-icon">
                                <SvgIcon name="mapPin" size={16} />
                              </div>
                              <div>
                                <span className="td-att-info-label">Venue</span>
                                <span className="td-att-info-val">
                                  {currentSchedule.Venue || (currentSchedule.CourseName.toLowerCase().includes('online') ? 'Online' : 'CCoE, Cyberjaya')}
                                </span>
                              </div>
                            </div>

                            <div className="td-att-info-item">
                              <div className="td-att-info-icon">
                                <SvgIcon name="users" size={16} />
                              </div>
                              <div>
                                <span className="td-att-info-label">Enrolled Learners</span>
                                <span className="td-att-info-val">{currentSchedule.EnrolledCount}</span>
                              </div>
                            </div>
                          </div>

                          {/* Integrated Live Class Session Code Generator Box */}
                          <div style={{ background: 'rgba(255, 255, 255, 0.08)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.15)', padding: '1.25rem 1.5rem' }}>
                            <div className="td-session-header" style={{ marginBottom: '1rem' }}>
                              <div className="td-session-icon" style={{ background: 'rgba(255, 255, 255, 0.15)', padding: '0.6rem', borderRadius: '8px' }}>
                                <SvgIcon name="lock" size={20} />
                              </div>
                              <div>
                                <h4 style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                                  Live Class Session Code Generator
                                </h4>
                                <p style={{ color: '#c7d2fe', fontSize: '0.825rem', margin: '0.2rem 0 0 0' }}>
                                  Generate a live check-in code for {formatLongDate(selectedAttendanceDate)}. Share this code with learners in class so they can self check-in from their Learner Dashboard.
                                </p>
                              </div>
                            </div>

                            <div className="td-session-body" style={{ background: 'rgba(0, 0, 0, 0.2)', border: '1px solid rgba(255, 255, 255, 0.15)', padding: '1rem 1.25rem', borderRadius: '10px' }}>
                              <div className="td-code-display-box">
                                <span className="td-code-label">Active Class Code ({formatLongDate(selectedAttendanceDate)}):</span>
                                {isCodeActive ? (
                                  <>
                                    <div className="td-code-badge-large">
                                      {activeSessionCode}
                                    </div>
                                    <span className="td-code-status-active">
                                      <span className="pulse-dot" /> Active &amp; Ready for Learner Self Check-In ({formatLongDate(selectedAttendanceDate)})
                                    </span>
                                    <div className="td-code-validity-info">
                                      <SvgIcon name="clock" size={14} />
                                      <span>Valid for: <strong className="td-countdown-text">{formatCountdown(remainingSeconds)}</strong> remaining</span>
                                      {codeExpiresAt && (
                                        <span className="td-expires-time">
                                          (Expires at: {formatTimeOnly(codeExpiresAt)})
                                        </span>
                                      )}
                                    </div>
                                  </>
                                ) : isCodeExpired ? (
                                  <>
                                    <div className="td-code-badge-large td-code-badge-expired">
                                      CODE EXPIRED
                                    </div>
                                    <span className="td-code-status-expired">
                                      <span className="expired-dot" /> Code Expired
                                    </span>
                                    <p className="td-code-expired-msg">
                                      Attendance code expired. Generate a new code to allow learner check-in.
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <div className="td-code-badge-large td-code-badge-empty">
                                      No Attendance Code Generated
                                    </div>
                                    <p className="td-code-empty-msg">
                                      {selectedAttendanceDate
                                        ? `Generate an attendance code for ${formatLongDate(selectedAttendanceDate)} to enable learner check-in.`
                                        : 'Generate an attendance code for this class to enable learner check-in.'}
                                    </p>
                                  </>
                                )}
                              </div>

                              <div className="td-code-actions">
                                <div className="td-validity-selector-wrapper">
                                  <label htmlFor="code-validity-select" className="td-validity-label">
                                    Code Validity:
                                  </label>
                                  <select
                                    id="code-validity-select"
                                    className="td-validity-select"
                                    value={selectedValidityDuration}
                                    onChange={(e) => setSelectedValidityDuration(Number(e.target.value))}
                                    disabled={generatingCode}
                                  >
                                    {availableValidityDurations.map(dur => (
                                      <option key={dur} value={dur}>{dur} minutes</option>
                                    ))}
                                  </select>
                                </div>

                                <button
                                  className="td-gen-code-btn"
                                  onClick={() => handleGenerateSessionCode(currentSchedule?.ScheduleID, selectedAttendanceDate)}
                                  disabled={generatingCode}
                                >
                                  <SvgIcon name="checkSquare" size={16} />
                                  {generatingCode ? 'Generating...' : isCodeActive ? 'Refresh / Generate New Code' : (activeSessionCode ? 'Refresh / Generate New Code' : 'Generate Attendance Code')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ATTENDANCE METRICS & ROSTER FOR SELECTED DATE */}
                        <div className="td-section-card">
                          <div className="td-att-stats-bar">
                            <div className="att-stat-item">
                              <span className="att-lbl">Total Registered</span>
                              <span className="att-val">{attendanceList.length}</span>
                            </div>
                            <div className="att-stat-item text-green">
                              <span className="att-lbl">Present</span>
                              <span className="att-val">{presentCount}</span>
                            </div>
                            <div className="att-stat-item text-red">
                              <span className="att-lbl">Absent</span>
                              <span className="att-val">{absentCount}</span>
                            </div>
                            <div className="att-stat-item highlight">
                              <span className="att-lbl">Attendance Rate</span>
                              <span className="att-val">{attendanceRate}%</span>
                            </div>
                          </div>

                          {/* TOOLBAR WITH FILTERS AND INLINE SEARCH */}
                          <div className="td-att-toolbar">
                            <div className="td-att-filters">
                              <button className={`att-filter-btn ${attendanceFilter === 'all' ? 'active' : ''}`} onClick={() => setAttendanceFilter('all')}>
                                All ({attendanceList.length})
                              </button>
                              <button className={`att-filter-btn ${attendanceFilter === 'Present' ? 'active' : ''}`} onClick={() => setAttendanceFilter('Present')}>
                                Present ({presentCount})
                              </button>
                              <button className={`att-filter-btn ${attendanceFilter === 'Absent' ? 'active' : ''}`} onClick={() => setAttendanceFilter('Absent')}>
                                Absent ({absentCount})
                              </button>
                            </div>

                            <div className="td-att-inline-search">
                              <SvgIcon name="search" size={14} />
                              <input
                                type="text"
                                placeholder="Search learner by name, email, or org..."
                                value={attendeeSearchQuery}
                                onChange={e => setAttendeeSearchQuery(e.target.value)}
                              />
                              {attendeeSearchQuery && (
                                <button
                                  className="td-att-search-clear"
                                  onClick={() => setAttendeeSearchQuery('')}
                                  title="Clear search"
                                >
                                  &times;
                                </button>
                              )}
                            </div>
                          </div>

                          {loadingAttendance ? (
                            <div className="profile-loading">
                              <div className="profile-loading-spinner" />
                              <p>Loading attendance roster for {formatLongDate(selectedAttendanceDate)}...</p>
                            </div>
                          ) : filteredAttendance.length === 0 ? (
                            <div className="td-empty-state">
                              <SvgIcon name="info" size={32} />
                              <p>No attendance records match your search or filter for {formatLongDate(selectedAttendanceDate)}.</p>
                            </div>
                          ) : (
                            <div className="td-table-responsive">
                              <table className="td-table">
                                <thead>
                                  <tr>
                                    <th>Learner Name</th>
                                    <th>Contact Email</th>
                                    <th>Organisation</th>
                                    <th>Status</th>
                                    <th>Check-In Time</th>
                                    <th>Mark Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredAttendance.map(item => (
                                    <tr key={item.AttendanceID}>
                                      <td className="font-bold">{item.FullName}</td>
                                      <td>{item.Email}</td>
                                      <td>{item.OrganisationName || 'Individual'}</td>
                                      <td>
                                        <span className={`td-status-badge att-${item.Status.toLowerCase()}`}>
                                          {item.Status}
                                        </span>
                                      </td>
                                      <td className="text-xs text-gray-500 font-mono">
                                        {item.CheckInTime ? item.CheckInTime : '-'}
                                      </td>
                                      <td>
                                        <div className="td-status-actions">
                                          <button
                                            className={`att-toggle-btn btn-pres ${item.Status === 'Present' ? 'active' : ''}`}
                                            onClick={() => handleMarkAttendance(item.AttendanceID, 'Present')}
                                          >
                                            Present
                                          </button>
                                          <button
                                            className={`att-toggle-btn btn-abs ${item.Status === 'Absent' ? 'active' : ''}`}
                                            onClick={() => handleMarkAttendance(item.AttendanceID, 'Absent')}
                                          >
                                            Absent
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}              {/* TAB 4: PROFILE & SECURITY */}
              {activeTab === 'profile' && (
                loading && !trainerProfile ? (
                  <div className="profile-loading">
                    <div className="profile-loading-spinner" />
                    <p>Loading profile...</p>
                  </div>
                ) : (
                  <div className="profile-body-grid">
                    {/* Left Column - Form & Details */}
                    <div className="profile-left-column">
                      {/* Personal Information */}
                      <div className="profile-section-card">
                        <div className="section-card-header">
                          <div className="section-card-icon">
                            <SvgIcon name="user" size={18} />
                          </div>
                          <h4 className="section-card-title">Personal Information</h4>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Full Name</span>
                          <span className="info-field-value">{trainerProfile?.fullName || user?.fullName || 'N/A'}</span>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Username</span>
                          <span className="info-field-value font-mono">{user?.username || trainerProfile?.username || 'N/A'}</span>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Email Address</span>
                          <span className="info-field-value">{trainerProfile?.email || user?.email}</span>
                          <div className="info-field-action">
                            <button
                              className="edit-btn"
                              onClick={() => {
                                setNewEmail('');
                                setConfirmEmail('');
                                setEmailError('');
                                setEmailModalOpen(true);
                              }}
                            >
                              <SvgIcon name="edit" size={13} /> Edit
                            </button>
                          </div>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">System Role</span>
                          <span className="info-field-value">{trainerProfile?.role || 'Staff Trainer'}</span>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Affiliation</span>
                          <span className="info-field-value">{trainerProfile?.organisationName || 'BlackBerry CCoE'}</span>
                        </div>
                      </div>

                      {/* Certifications & Biography Card */}
                      <div className="profile-section-card">
                        <div className="section-card-header">
                          <div className="section-card-icon">
                            <SvgIcon name="award" size={18} />
                          </div>
                          <h4 className="section-card-title">Certifications & Biography</h4>
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                          <label style={{ display: 'block', fontWeight: 600, color: '#1e293b', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                            Certifications
                          </label>
                          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.78rem', color: '#64748b' }}>
                            Separate certifications with commas (e.g. CompTIA CySA+, Security+, CEH)
                          </p>
                          <input
                            type="text"
                            className="td-input-enhanced"
                            placeholder="e.g. CompTIA Security+, CISSP, CySA+"
                            value={certificationsInput}
                            onChange={(e) => setCertificationsInput(e.target.value)}
                          />
                          {certificationsInput.trim() && (
                            <div className="cert-pill-container">
                              {certificationsInput.split(',').map((cert, idx) => {
                                const cleanCert = cert.trim();
                                if (!cleanCert) return null;
                                return (
                                  <span key={idx} className="cert-pill-badge">
                                    {cleanCert}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div>
                          <label style={{ display: 'block', fontWeight: 600, color: '#1e293b', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                            Biography
                          </label>
                          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.78rem', color: '#64748b' }}>
                            Briefly describe your expertise, security domains, and training background
                          </p>
                          <textarea
                            rows={4}
                            className="td-input-enhanced"
                            placeholder="Briefly describe your expertise, security domains, and training background..."
                            value={bioInput}
                            onChange={(e) => setBioInput(e.target.value)}
                            style={{ resize: 'vertical' }}
                          />
                        </div>
                      </div>

                      {/* Footer Action Buttons */}
                      <div className="profile-form-footer-actions">
                        <button
                          className="btn-save-changes"
                          onClick={handleSaveBioAndCerts}
                          disabled={savingBio}
                          data-testid="trainer-save-profile-btn"
                        >
                          {savingBio ? 'Saving Changes...' : 'Save Changes'}
                        </button>
                        <button
                          className="btn-cancel-changes"
                          type="button"
                          onClick={fetchTrainerBioAndCerts}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>

                    {/* Right Column - Avatar & Metadata */}
                    <div className="profile-right-column">
                      {/* Premium User Summary Card */}
                      <div className="profile-summary-side-card">
                        <div className="profile-avatar-empty-circle">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                          </svg>
                        </div>

                        <div className="profile-summary-side-details">
                          <h3>{trainerProfile?.fullName || user?.fullName}</h3>
                          <span className="profile-summary-role-badge">
                            Staff Trainer
                          </span>
                        </div>

                        <div className="profile-summary-side-metadata">
                          <div className="metadata-row">
                            <span className="metadata-label">Member since</span>
                            <span className="metadata-value">
                              {trainerProfile?.createdAt ? formatDate(trainerProfile.createdAt) : '22/06/2026'}
                            </span>
                          </div>
                          <div className="metadata-row">
                            <span className="metadata-label">Last login</span>
                            <span className="metadata-value">
                              {trainerProfile?.lastLoginDate ? formatDateTimeString(trainerProfile.lastLoginDate) : lastLoginTime}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Teaching Overview Card */}
                      <div className="profile-summary-side-card project-code-side-card" style={{ marginTop: '1.5rem' }}>
                        <div className="project-code-header" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                          <div style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', padding: '0.5rem', borderRadius: '50%', display: 'flex', color: '#6366f1' }}>
                            <SvgIcon name="award" size={18} />
                          </div>
                          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>Teaching Overview</h3>
                        </div>
                        <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                          Overview of your active training course assignments and instructional metrics.
                        </p>

                        <div className="profile-summary-side-metadata" style={{ borderTop: 'none', paddingTop: 0 }}>
                          <div className="metadata-row">
                            <span className="metadata-label">Assigned Courses</span>
                            <span className="metadata-value">{courses.length}</span>
                          </div>
                          <div className="metadata-row">
                            <span className="metadata-label">Enrolled Learners</span>
                            <span className="metadata-value">{totalStudents}</span>
                          </div>
                          <div className="metadata-row">
                            <span className="metadata-label">Teaching Days</span>
                            <span className="metadata-value">{totalDaysTaught}</span>
                          </div>
                          <div className="metadata-row">
                            <span className="metadata-label">Account Status</span>
                            <span className="metadata-value">
                              <span style={{
                                backgroundColor: '#ecfdf5',
                                color: '#10b981',
                                fontSize: '12px',
                                fontWeight: '600',
                                padding: '2px 8px',
                                borderRadius: '10px'
                              }}>
                                Active
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>

      {/* CLASS ROSTER MODAL */}
      {rosterModalOpen && selectedSchedule && (
        <div className="td-modal-overlay" onClick={() => setRosterModalOpen(false)}>
          <div className="td-modal-content" onClick={e => e.stopPropagation()}>
            <button className="td-modal-close" onClick={() => setRosterModalOpen(false)}>
              <SvgIcon name="x" size={18} />
            </button>
            <div className="td-modal-header">
              <span className="td-modal-pretitle">{selectedSchedule.CertificationBody}</span>
              <h2>{selectedSchedule.CourseName}</h2>
              {(() => {
                const modalSegs = parseCourseSegments(selectedSchedule);
                if (modalSegs && modalSegs.length > 1) {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '4px' }}>
                      {modalSegs.map((s, idx) => (
                        <span key={idx} style={{ fontSize: '0.825rem', color: '#475569' }}>
                          <strong style={{ color: '#4f46e5', marginRight: '4px' }}>Part {idx + 1}:</strong>
                          {formatDateDisplay(s.startDate)} - {formatDateDisplay(s.endDate)} ({s.startTime || selectedSchedule.StartTime} - {s.endTime || selectedSchedule.EndTime})
                        </span>
                      ))}
                      <span style={{ fontSize: '0.75rem', color: '#047857', fontWeight: 600 }}>
                        Total: {getCourseActiveDates(selectedSchedule).length} Training Days
                      </span>
                    </div>
                  );
                }
                return (
                  <p className="td-modal-schedule-info">
                    <SvgIcon name="calendar" size={14} /> {formatDateDisplay(selectedSchedule.StartDate)} - {formatDateDisplay(selectedSchedule.EndDate)}
                    <span className="td-modal-divider">|</span>
                    <SvgIcon name="clock" size={14} /> {selectedSchedule.StartTime} - {selectedSchedule.EndTime}
                  </p>
                );
              })()}
            </div>

            <div className="td-modal-body">
              {loadingRoster ? (
                <div className="td-modal-loading">
                  <div className="profile-loading-spinner" />
                  <p>Loading class roster...</p>
                </div>
              ) : roster.length === 0 ? (
                <div className="td-modal-empty">
                  <SvgIcon name="users" size={32} />
                  <p>No learners have registered for this session yet.</p>
                </div>
              ) : (
                <div className="td-modal-table-wrap">
                  <table className="td-modal-table">
                    <thead>
                      <tr>
                        <th>Learner Name</th>
                        <th>Email Address</th>
                        <th>Contact Number</th>
                        <th>Organisation / Sector</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roster.map((learner, idx) => (
                        <tr key={idx}>
                          <td className="font-bold">{learner.FullName}</td>
                          <td>{learner.Email}</td>
                          <td className="font-mono text-xs">{learner.Contact}</td>
                          <td>
                            <div className="td-learner-org-cell">
                              <span className="org-lbl">{learner.OrganisationName || 'N/A'}</span>
                              <span className="sector-lbl">{learner.Sector || 'N/A'}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`td-status-badge ${learner.Status?.toLowerCase() || 'approved'}`}>
                              {learner.Status || 'Approved'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="td-modal-footer">
              <span>Total Learners Registered: <strong>{roster.length}</strong></span>
              <button className="td-modal-btn-close" onClick={() => setRosterModalOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Refresh / Generate New Code while code is active */}
      {confirmRegenModalOpen && (
        <div className="td-modal-overlay" onClick={() => setConfirmRegenModalOpen(false)}>
          <div className="td-modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <button className="td-modal-close" onClick={() => setConfirmRegenModalOpen(false)}>
              <SvgIcon name="x" size={18} />
            </button>
            <div className="td-modal-header" style={{ borderBottom: 'none', paddingBottom: '0.25rem' }}>
              <h3 style={{ fontSize: '1.15rem', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <SvgIcon name="alertCircle" size={20} color="#f59e0b" />
                Generate New Attendance Code?
              </h3>
            </div>
            <div className="td-modal-body" style={{ padding: '0.75rem 0 1.25rem 0' }}>
              <p style={{ color: '#475569', fontSize: '0.925rem', lineHeight: '1.5', margin: 0 }}>
                Generate a new attendance code? The current code will become invalid immediately.
              </p>
              <p style={{ color: '#64748b', fontSize: '0.825rem', marginTop: '0.75rem', marginBottom: 0 }}>
                Selected validity: <strong>{selectedValidityDuration} minutes</strong>.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                className="td-modal-btn-close"
                style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
                onClick={() => setConfirmRegenModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="td-gen-code-btn"
                style={{ background: '#4f46e5', color: '#ffffff', padding: '0.55rem 1.25rem', fontSize: '0.875rem' }}
                onClick={() => {
                  setConfirmRegenModalOpen(false);
                  executeGenerateSessionCode(selectedAttendanceSchedule?.ScheduleID, selectedAttendanceDate, selectedValidityDuration);
                }}
              >
                Generate New Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT EMAIL MODAL */}
      {emailModalOpen && (
        <div className="profile-modal-overlay" onClick={() => setEmailModalOpen(false)}>
          <div className="profile-modal-card" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Email Address</h3>
              <button className="modal-close-btn" onClick={() => setEmailModalOpen(false)}>
                <SvgIcon name="x" size={20} />
              </button>
            </div>

            <form onSubmit={handleEmailUpdate}>
              <div className="modal-body">
                {emailError && (
                  <p className="modal-field-error" style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '1rem', backgroundColor: '#fee2e2', padding: '0.5rem 0.75rem', borderRadius: '6px' }}>
                    {emailError}
                  </p>
                )}
                <div className="modal-field">
                  <label>Current Email</label>
                  <input type="email" value={trainerProfile?.email || user?.email || ''} readOnly />
                </div>
                <div className="modal-field">
                  <label htmlFor="newEmail">New Email Address</label>
                  <input
                    type="email"
                    id="newEmail"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    required
                    placeholder="Enter new email"
                  />
                </div>
                <div className="modal-field">
                  <label htmlFor="confirmEmail">Confirm New Email Address</label>
                  <input
                    type="email"
                    id="confirmEmail"
                    value={confirmEmail}
                    onChange={e => setConfirmEmail(e.target.value)}
                    required
                    placeholder="Confirm new email"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="submit"
                  className="modal-btn-primary"
                  disabled={emailSubmitting}
                >
                  {emailSubmitting ? 'Updating...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="modal-btn-secondary"
                  onClick={() => setEmailModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FIRST LOGIN PASSWORD RESET MODAL */}
      {firstLoginModalOpen && (
        <div className="td-modal-overlay">
          <div className="td-modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="td-modal-header" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', padding: '12px', background: '#e0e7ff', color: '#4f46e5', borderRadius: '50%', marginBottom: '12px' }}>
                <SvgIcon name="lock" size={28} />
              </div>
              <h2 style={{ fontSize: '1.4rem' }}>First Login - Password Reset</h2>
              <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                Welcome to Cloudonuts Trainer Dashboard! As this is your first login, please set a new secure password for your account.
              </p>
            </div>

            <form onSubmit={handleFirstLoginPasswordReset}>
              <div className="td-form-body">
                {firstLoginError && (
                  <div className="td-form-error">
                    {firstLoginError}
                  </div>
                )}
                <div className="td-form-group">
                  <label htmlFor="flPass">New Password</label>
                  <input
                    type="password"
                    id="flPass"
                    value={firstLoginPassword}
                    onChange={e => setFirstLoginPassword(e.target.value)}
                    required
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                <div className="td-form-group">
                  <label htmlFor="flConfirmPass">Confirm New Password</label>
                  <input
                    type="password"
                    id="flConfirmPass"
                    value={firstLoginConfirmPassword}
                    onChange={e => setFirstLoginConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <button
                  type="submit"
                  className="td-modal-btn-primary"
                  style={{ width: '100%', padding: '12px', fontSize: '1rem', fontWeight: 'bold' }}
                  disabled={firstLoginSubmitting}
                >
                  {firstLoginSubmitting ? 'Updating Password...' : 'Set New Password & Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerDashboard;
